/** @jsxImportSource @emotion/react */
import React, { useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import { IAdminSourceData, TAdminSourceSort } from '@datatype/admin/api-type';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as adminActions } from '@store/slice/admin-slice';
import { useAdminSourcesQuery, useDeleteAdminSourceMutation } from '@hook/admin/useApi';
import { useInfiniteScroll } from '@hook/common/useScroll';
import ExpressionPreview from '@component/admin/expression/expression-home/expression-preview';
import ExpressionSourceFilter from '@component/admin/expression/expression-home/expression-source-filter';
import ExpressionSourceList from '@component/admin/expression/expression-home/expression-source-list';
import ExpressionMetricPanel from '@component/admin/expression/expression-metric-panel';
import ExpressionCreateModal from '@component/admin/expression-create-modal';
import ExpressionSourceRemoveModal from '@component/admin/expression-source-remove-modal';
import { queryClient } from '@config/query-client';
import { EAdminQueryKey } from '@constant/admin/query-key';

const PER_PAGE = 20;

const Wrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 16px;
  width: 100%;
  height: 100%;
  padding: 24px;
  box-sizing: border-box;
`;

const MetricPane = styled.div`
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
`;

const CenterPane = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  width: 600px;
  height: 100%;
  overflow-y: auto;
`;

const RightPane = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  flex: 1;
  height: 100%;
  overflow-y: auto;
`;

const ExpressionHome: React.FC = () => {
  const dispatch = useTypedDispatch();
  const selectedSourceKey =
    useTypedSelector((state) => state.rootReducer?.adminReducer?.selectedSourceKey) ?? null;
  const filter = useTypedSelector(
    (state) =>
      state.rootReducer?.adminReducer?.expressionFilter ?? {
        languageCode: null,
        sortBy: null,
        sortOrder: 'desc' as const,
      },
  );
  const { languageCode, sortBy, sortOrder } = filter;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<IAdminSourceData | null>(null);
  const [removeErrorMessage, setRemoveErrorMessage] = useState<string | null>(null);
  const centerPaneRef = useRef<HTMLDivElement | null>(null);

  const sourcesQuery = useAdminSourcesQuery({
    perPage: PER_PAGE,
    languageCode,
    sortBy,
    sortOrder: sortBy ? sortOrder : null,
  });
  const deleteSourceMutation = useDeleteAdminSourceMutation();

  const sources = sourcesQuery.data?.pages.flatMap((p) => p.data ?? []) ?? [];

  const { sentinelRef } = useInfiniteScroll({
    hasNextPage: sourcesQuery.hasNextPage ?? false,
    isFetchingNextPage: sourcesQuery.isFetchingNextPage ?? false,
    fetchNextPage: sourcesQuery.fetchNextPage,
    root: centerPaneRef,
  });

  const selectedSource: IAdminSourceData | null = useMemo(() => {
    if (!selectedSourceKey) return null;
    return sources.find((s) => s.key === selectedSourceKey) ?? null;
  }, [sources, selectedSourceKey]);

  const handleSelect = (source: IAdminSourceData) => {
    dispatch(adminActions.storeSelectedSourceKey(source.key));
  };

  const handleDelete = (source: IAdminSourceData) => {
    setRemoveErrorMessage(null);
    setRemoveTarget(source);
  };

  const handleRemoveClose = () => {
    if (deleteSourceMutation.isPending) return;
    setRemoveErrorMessage(null);
    setRemoveTarget(null);
  };

  const handleRemoveConfirm = async () => {
    if (!removeTarget) return;
    setRemoveErrorMessage(null);

    try {
      await deleteSourceMutation.mutateAsync(removeTarget.key);
      if (selectedSourceKey === removeTarget.key) {
        dispatch(adminActions.storeSelectedSourceKey(null));
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [EAdminQueryKey.ADMIN_SOURCES] }),
        queryClient.invalidateQueries({ queryKey: [EAdminQueryKey.ADMIN_SOURCE, removeTarget.key] }),
        queryClient.invalidateQueries({ queryKey: [EAdminQueryKey.ADMIN_EXPRESSION_METRIC] }),
      ]);
      setRemoveTarget(null);
    } catch {
      setRemoveErrorMessage('삭제에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  const handleLanguageChange = (lc: EApiLanguageCode | null) => {
    dispatch(adminActions.storeExpressionFilter({ languageCode: lc }));
  };

  const handleSortClick = (field: TAdminSourceSort) => {
    if (sortBy === field) {
      dispatch(
        adminActions.storeExpressionFilter({
          sortOrder: sortOrder === 'asc' ? 'desc' : 'asc',
        }),
      );
    } else {
      dispatch(adminActions.storeExpressionFilter({ sortBy: field, sortOrder: 'desc' }));
    }
  };

  return (
    <Wrapper>
      <MetricPane>
        <ExpressionMetricPanel />
      </MetricPane>
      <CenterPane ref={centerPaneRef}>
        <ExpressionSourceFilter
          languageCode={languageCode}
          sortBy={sortBy}
          sortOrder={sortOrder}
          handleChange={handleLanguageChange}
          handleSortClick={handleSortClick}
        />
        <ExpressionSourceList
          sources={sources}
          selectedSourceKey={selectedSourceKey}
          isLoading={sourcesQuery.isLoading}
          isFetchingNextPage={sourcesQuery.isFetchingNextPage ?? false}
          isError={sourcesQuery.isError}
          handleSelect={handleSelect}
          handleDelete={handleDelete}
          deletingSourceKey={
            deleteSourceMutation.isPending ? (deleteSourceMutation.variables ?? null) : null
          }
          handleRetry={sourcesQuery.refetch}
          sentinelRef={sentinelRef}
        />
      </CenterPane>
      <RightPane>
        <ExpressionPreview
          selectedSource={selectedSource}
          handleCreateClick={() => setIsCreateOpen(true)}
        />
      </RightPane>
      <ExpressionCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        initialSourceKey={selectedSource?.key ?? null}
        initialLanguageCode={selectedSource ? (selectedSource.languageCode as EApiLanguageCode) : null}
      />
      <ExpressionSourceRemoveModal
        isOpen={!!removeTarget}
        sourceTitle={removeTarget?.title ?? null}
        isPending={deleteSourceMutation.isPending}
        errorMessage={removeErrorMessage}
        onClose={handleRemoveClose}
        onConfirm={handleRemoveConfirm}
      />
    </Wrapper>
  );
};

export default ExpressionHome;
